import { PlaybackError } from '../../errors';
import { SeekResultType, } from '../types/seek-result';
import { RateResult } from '../types/rate-result';
import { ErrorCode } from '../../errors/enums/error-code';
import { EventKey as TelemetryEventKey } from '../../telemetry/enums/event-key';
import { AdEventKey, EventKey, PlaybackState, } from '../enums';
import { AdType, } from '../types';
import { debounce } from '../../utils';
// @todo We should remove the seek timeout,
// but for now we increase timeout to avoid gt12 ads not showing after seek
// https://zattoo2.atlassian.net/browse/PLY-2102
const SEEKED_TIMEOUT = 8_000;
// threshold to seekable range borders to disable seek permissions
export const SEEK_BACK_MARGIN = 5;
export const SEEK_FORWARD_MARGIN = 1;
/**
 * Debounce seek calls by 500ms
 */
const SEEK_DEBOUNCE_MS = 500;
/**
 * After 5 minutes of cumulative seeking (in stream position difference),
 * we will start to dispatch throttled seek events.
 */
const SEEK_BEGIN_THROTTLE = 60 * 5;
/**
 * Trigger throttled seek events every 5 seconds (wall clock)
 */
const SEEK_THROTTLE_MS = 1000 * 5;
const getSafeSeekPosition = (position, seekableRange) => {
    if (position <= seekableRange.start) {
        return seekableRange.start;
    }
    if (position > seekableRange.end) {
        return seekableRange.end;
    }
    return position;
};
const getNewSeekPosition = (currentPosition, offset, seekPosition) => {
    if (seekPosition == null) {
        return currentPosition + offset;
    }
    return seekPosition + offset;
};
export const WithControl = (Base) => {
    return class PlayerWithControl extends Base {
        #cumulativeSeekOffset = null;
        pauseAdTimeout = null;
        clearPauseAdTimeout() {
            if (this.pauseAdTimeout) {
                this._logger?.info(`PauseAd, clearing timeout id=${this.pauseAdTimeout}`);
                clearTimeout(this.pauseAdTimeout);
                this.pauseAdTimeout = null;
            }
        }
        dispatchMedia(media, noEvent) {
            this.schedulePauseAdIfNeeded();
            return super.dispatchMedia(media, noEvent);
        }
        schedulePauseAdIfNeeded() {
            if (!this._playOptions?.adsInfo?.pause) {
                return;
            }
            if (!this._isPausedByUser) {
                return;
            }
            this.schedulePauseAd({
                ...this._playOptions.adsInfo.pause,
                type: AdType.PAUSE,
            });
        }
        schedulePauseAd(pauseAd) {
            this.clearPauseAdTimeout();
            if (!pauseAd || !pauseAd.vastUrl || pauseAd.offset === undefined) {
                this._logger?.info('Invalid pause ad configuration:', pauseAd);
                return;
            }
            this.triggerEvent({
                type: AdEventKey.PAUSE_AD_STREAM_PAUSED,
            });
            this.pauseAdTimeout = setTimeout(() => {
                this._logger?.info(`PauseAd, timeout finished, requesting vast ', ${JSON.stringify(pauseAd)}`);
                this.adapter.loadPauseVast(pauseAd.vastUrl);
                this.triggerEvent({
                    type: AdEventKey.PAUSE_AD_REQUESTED,
                });
            }, pauseAd.offset * 1000);
            this._logger?.info(`PauseAd, Scheduled pause ad timer, id=${this.pauseAdTimeout}, time: ${pauseAd.offset}s`);
        }
        play() {
            this.adapter.play();
            this._isPausedByUser = false;
            this._logger?.info('PauseAd, Clearing pauseAdTimeout from Control.ts play');
            this.clearPauseAdTimeout();
        }
        pause() {
            if (!this._canPause) {
                this.handleError(new PlaybackError(ErrorCode.ForbiddenAction, 'Pause is not allowed'));
                return;
            }
            this._isPausedByUser = true;
            this.schedulePauseAdIfNeeded();
            this.adapter.pause();
        }
        stop() {
            this._playbackStateInitial = true;
            this._seekableRange = null;
            this.clearPauseAdTimeout();
            if (this._playbackState === PlaybackState.STOPPED) {
                return Promise.resolve();
            }
            return new Promise((resolve) => {
                const onPlaybackStateChanged = (event) => {
                    if (event.state !== PlaybackState.STOPPED) {
                        return;
                    }
                    this.offInternal(EventKey.PLAYBACK_STATE_CHANGED, onPlaybackStateChanged);
                    resolve();
                };
                this.onInternal(EventKey.PLAYBACK_STATE_CHANGED, onPlaybackStateChanged);
                this.adapter.stop().catch((error) => {
                    this.handleError(error);
                });
            }).catch((error) => {
                this.handleError(error);
            });
        }
        #updateSeekPosition(seekPosition) {
            this.seekPosition = seekPosition;
        }
        #updateCumulativeSeekOffset(offset) {
            if (!this.#cumulativeSeekOffset) {
                this.#cumulativeSeekOffset = 0;
            }
            this.#cumulativeSeekOffset += offset;
            if (this.#cumulativeSeekOffset >= SEEK_BEGIN_THROTTLE) {
                this.#debouncedSeek.setMaxWait(SEEK_THROTTLE_MS);
            }
        }
        #validateCanSeek(forward) {
            return forward ? this._canSeekForward : this._canSeekBackward;
        }
        #validateCanSeekByPosition(seekPosition) {
            const currentPosition = this.getCurrentPosition();
            if (currentPosition === null) {
                return false;
            }
            const forward = seekPosition > currentPosition;
            return this.#validateCanSeek(forward);
        }
        #safeSeekPosition(seekPosition) {
            if (!this._seekableRange) {
                return null;
            }
            return getSafeSeekPosition(seekPosition, this._seekableRange);
        }
        #validateGT12SeekPosition(seekPosition) {
            const currentPosition = this.getCurrentPosition();
            if (currentPosition == null) {
                this._logger?.error('Current position is null, cannot seek');
                return {
                    type: SeekResultType.FORWARD_SEEKING_NOT_ALLOWED,
                };
            }
            const result = this.seekController?.canSeek(currentPosition, seekPosition, this._playbackState);
            if (result) {
                return result;
            }
            return {
                type: SeekResultType.ALLOWED,
                seekPosition,
            };
        }
        #debouncedSeek = debounce((position) => {
            return this.seek(position);
        }, {
            delay: SEEK_DEBOUNCE_MS,
        });
        cleanupCumulativeSeek() {
            // this.#cumulativeSeekOffset = null;
            this.seekPosition = null;
            this.#debouncedSeek.cancel();
        }
        #handleSeek(seekResult) {
            switch (seekResult.type) {
                case SeekResultType.FORWARD_SEEKING_NOT_ALLOWED:
                case SeekResultType.GT12_FORBIDDEN:
                case SeekResultType.STREAM_CHANGE:
                    return;
                default:
                    break;
            }
            // Better not break anything for GT12 with continuous seeking
            const noFurtherSeekAllowed = seekResult.type === SeekResultType.GT12_SEEK_TO_ADBREAK ||
                seekResult.type === SeekResultType.GT12_SEEK_TO_AND_DISPLAY_ADBREAK;
            const displayAdBreak = seekResult.type === SeekResultType.GT12_SEEK_TO_AND_DISPLAY_ADBREAK;
            const seekedHandler = () => {
                this.off(EventKey.SEEKED, seekedHandler);
                this.setPlayerState([]);
                // This can happen if we had cumulative seeks while the player was seeking
                const isUserCumulativeScrubbing = this.#debouncedSeek.isPending();
                // no user intention to further seek, or not allowed
                if (!isUserCumulativeScrubbing || noFurtherSeekAllowed) {
                    this.cleanupCumulativeSeek();
                }
                if (displayAdBreak) {
                    const ad = {
                        type: AdType.FAST_FORWARD,
                        vastUrl: seekResult.vastUrl,
                        isChannelAd: seekResult.isChannelAd,
                        maxDuration: seekResult.maxDuration,
                    };
                    this.adapter.loadFastForwardVast(ad);
                }
                else {
                    this.schedulePauseAdIfNeeded();
                }
            };
            this.ephemeral(EventKey.SEEKED, seekedHandler, SEEKED_TIMEOUT);
            if (this.psid) {
                this.triggerTelemetryEvent({
                    type: TelemetryEventKey.SEEK,
                    psid: this.psid,
                });
            }
            this.triggerEvent({
                type: EventKey.CURRENT_POSITION_CHANGED,
                position: seekResult.seekPosition,
            });
            this.setPlayerState([]);
            this.adapter.seek(seekResult.seekPosition);
        }
        seek(position) {
            const notAllowedResult = {
                type: SeekResultType.FORWARD_SEEKING_NOT_ALLOWED,
            };
            if (!this.#validateCanSeekByPosition(position)) {
                return notAllowedResult;
            }
            const safeSeekPosition = this.#safeSeekPosition(position);
            if (safeSeekPosition === null) {
                return notAllowedResult;
            }
            const seekResult = this.#validateGT12SeekPosition(safeSeekPosition);
            this.#handleSeek(seekResult);
            return seekResult;
        }
        seekBySteps(steps) {
            const forward = steps > 0;
            const notAllowedResult = {
                type: SeekResultType.FORWARD_SEEKING_NOT_ALLOWED,
            };
            if (!this.#validateCanSeek(forward)) {
                return Promise.resolve(notAllowedResult);
            }
            const currentPosition = this.getCurrentPosition();
            if (currentPosition === null) {
                const error = new PlaybackError(ErrorCode.Unknown, 'Current position is not available');
                this.handleError(error);
                return Promise.reject(error);
            }
            const stepDuration = forward
                ? this._playerOptions.stepForwardDuration
                : this._playerOptions.stepBackwardDuration;
            const offset = steps * stepDuration;
            const newSeekPosition = getNewSeekPosition(currentPosition, offset, this.seekPosition);
            const safeSeekPosition = this.#safeSeekPosition(newSeekPosition);
            if (!safeSeekPosition) {
                return Promise.resolve(notAllowedResult);
            }
            this.#updateSeekPosition(safeSeekPosition);
            this.#updateCumulativeSeekOffset(offset);
            this.triggerEvent({
                type: EventKey.CURRENT_POSITION_CHANGED,
                position: safeSeekPosition,
            });
            return this.#debouncedSeek(safeSeekPosition).catch(() => {
                return notAllowedResult;
            });
        }
        seekBackward(steps) {
            return this.seekBySteps(-steps);
        }
        seekForward(steps) {
            return this.seekBySteps(steps);
        }
        setVolume(value) {
            this._volume = value;
            this.adapter.setVolume(value);
            if (!this.psid) {
                return;
            }
            this.triggerTelemetryEvent({
                type: TelemetryEventKey.VOLUME_CHANGED,
                psid: this.psid,
                volume: value,
            });
        }
        // @ts-expect-error unused
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        setRate(rate) {
            this._logger?.info('not implemented: setRate');
            return RateResult.ALLOWED;
        }
        seekToAdBreakEnd() {
            const adBreakEnd = this.seekController?.getAdBreakEnd();
            if (adBreakEnd === undefined) {
                return {
                    type: SeekResultType.GT12_FORBIDDEN,
                };
            }
            return this.seek(adBreakEnd);
        }
        thumbnailData(time) {
            return this.adapter.thumbnailData(time);
        }
        requestAdSkip() {
            this.adapter.requestAdSkip?.();
        }
        destroy() {
            this.clearPauseAdTimeout();
            return super.destroy();
        }
    };
};
